import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Plus, X } from "lucide-react";
import { appClient } from "@/api/appClient";
import { toast } from "sonner";

export default function SalesItemModal({ isOpen, onClose, onSuccess, editItem = null }) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    item_type: "case",
    pricing_type: "flat",
    default_price: 0,
    has_cost: false,
    default_cost: 0,
    is_active: true,
    config: {
      // Available roles for this case type
      available_roles: [],
      show_dependents: true,
      dependent_label: "Dependent",
    }
  });

  useEffect(() => {
    if (editItem) {
      setFormData({
        name: editItem.name || "",
        description: editItem.description || "",
        item_type: editItem.item_type || "case",
        pricing_type: editItem.pricing_type || "flat",
        default_price: editItem.default_price || 0,
        has_cost: editItem.has_cost || false,
        default_cost: editItem.default_cost || 0,
        is_active: editItem.is_active !== undefined ? editItem.is_active : true,
        config: {
          available_roles: editItem.config?.available_roles || [],
          show_dependents: editItem.config?.show_dependents !== undefined ? editItem.config.show_dependents : true,
          dependent_label: editItem.config?.dependent_label || "Dependent",
        }
      });
    } else {
      setFormData({
        name: "",
        description: "",
        item_type: "case",
        pricing_type: "flat",
        default_price: 0,
        has_cost: false,
        default_cost: 0,
        is_active: true,
        config: {
          available_roles: [],
          show_dependents: true,
          dependent_label: "Dependent",
        }
      });
    }
  }, [editItem, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);

    try {
      const functionName = editItem ? 'updateSalesItem' : 'createSalesItem';
      const payload = editItem 
        ? { id: editItem.id, ...formData }
        : formData;

      const response = await appClient.functions.invoke(functionName, payload);

      if (response.data.success) {
        toast.success(editItem ? 'Item updated successfully!' : 'Item created successfully!');
        onSuccess();
        onClose();
      } else {
        toast.error(response.data.error || 'Failed to save item');
      }
    } catch (error) {
      console.error("Error saving item:", error);
      toast.error(error.message);
    }

    setIsProcessing(false);
  };

  const updateConfig = (key, value) => {
    setFormData(prev => ({
      ...prev,
      config: {
        ...prev.config,
        [key]: value
      }
    }));
  };

  const addRole = () => {
    const newRole = { value: "", label: "" };
    updateConfig('available_roles', [...formData.config.available_roles, newRole]);
  };

  const updateRole = (index, field, value) => {
    const updated = [...formData.config.available_roles];
    updated[index][field] = value;
    updateConfig('available_roles', updated);
  };

  const removeRole = (index) => {
    const updated = formData.config.available_roles.filter((_, i) => i !== index);
    updateConfig('available_roles', updated);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editItem ? 'Edit' : 'Create'} Sales Item</DialogTitle>
          <DialogDescription>
            {formData.item_type === 'case' ? 'Configure a case type' : 'Configure a service'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-slate-900">Basic Information</h3>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Type *</Label>
                <Select
                  value={formData.item_type}
                  onValueChange={(value) => setFormData({ ...formData, item_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="case">Case</SelectItem>
                    <SelectItem value="service">Service</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Status</Label>
                <div className="flex items-center gap-2 h-10">
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                  <span className="text-sm text-slate-600">
                    {formData.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Family-Based Immigration"
                required
              />
            </div>

            <div>
              <Label>Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description..."
                rows={3}
              />
            </div>
          </div>

          <Separator />

          {/* Pricing */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-slate-900">Pricing</h3>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Pricing Type *</Label>
                <Select
                  value={formData.pricing_type}
                  onValueChange={(value) => setFormData({ ...formData, pricing_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="flat">Flat Rate</SelectItem>
                    <SelectItem value="variable">Variable (per person/unit)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Default Price *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.default_price}
                  onChange={(e) => setFormData({ ...formData, default_price: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                  required
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Switch
                checked={formData.has_cost}
                onCheckedChange={(checked) => setFormData({ ...formData, has_cost: checked })}
              />
              <Label>Track costs (for profit margin calculation)</Label>
            </div>

            {formData.has_cost && (
              <div>
                <Label>Default Cost</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.default_cost}
                  onChange={(e) => setFormData({ ...formData, default_cost: parseFloat(e.target.value) || 0 })}
                  placeholder="0.00"
                />
              </div>
            )}
          </div>

          {/* Roles Configuration - Only for Cases */}
          {formData.item_type === 'case' && (
            <>
              <Separator />
              <div className="space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-slate-900 mb-1">Roles Configuration</h3>
                  <p className="text-xs text-slate-500">
                    Define which roles people can have in this case type (e.g., Petitioner, Beneficiary, Applicant)
                  </p>
                </div>

                <div className="space-y-3">
                  {formData.config.available_roles.map((role, index) => (
                    <div key={index} className="flex gap-2 items-start p-3 bg-slate-50 rounded-lg">
                      <div className="flex-1 grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs">Role Value</Label>
                          <Input
                            placeholder="e.g., petitioner"
                            value={role.value}
                            onChange={(e) => updateRole(index, 'value', e.target.value)}
                            className="h-9"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Display Label</Label>
                          <Input
                            placeholder="e.g., Petitioner"
                            value={role.label}
                            onChange={(e) => updateRole(index, 'label', e.target.value)}
                            className="h-9"
                          />
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeRole(index)}
                        className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 mt-5"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>

                <Button
                  type="button"
                  variant="outline"
                  onClick={addRole}
                  className="w-full"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Role
                </Button>

                {/* Dependents */}
                <div className="p-4 bg-slate-50 rounded-lg space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-slate-900">Show Dependents</Label>
                      <p className="text-xs text-slate-500">
                        Allow adding dependents to this case type
                      </p>
                    </div>
                    <Switch
                      checked={formData.config.show_dependents}
                      onCheckedChange={(checked) => updateConfig('show_dependents', checked)}
                    />
                  </div>

                  {formData.config.show_dependents && (
                    <div>
                      <Label>Dependent Label *</Label>
                      <Input
                        value={formData.config.dependent_label}
                        onChange={(e) => updateConfig('dependent_label', e.target.value)}
                        placeholder="Dependent"
                      />
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isProcessing}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isProcessing ? 'Saving...' : editItem ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}